<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { minValue, maxValue } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ContactPicker from 'dashboard/components-next/CRM/Documents/ContactPicker/ContactPicker.vue';
import ChannelPicker from 'dashboard/components-next/CRM/Documents/ChannelPicker/ChannelPicker.vue';

const props = defineProps({ isSubmitting: { type: Boolean, default: false } });
const emit = defineEmits(['create', 'close']);
const { t } = useI18n();

const dialogRef = ref(null);
const contactPickerRef = ref(null);
const createdUrl = ref('');
const dispatchStatus = ref(null);
const form = ref({
  contactId: '',
  contactInboxId: '',
  message: '',
  expiresInDays: 7,
});

const rules = {
  expiresInDays: { minValue: minValue(1), maxValue: maxValue(90) },
};

const v$ = useVuelidate(rules, form);

const reset = () => {
  form.value = { contactId: '', contactInboxId: '', message: '', expiresInDays: 7 };
  createdUrl.value = '';
  dispatchStatus.value = null;
  v$.value.$reset();
};

const open = () => {
  reset();
  contactPickerRef.value?.load();
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

// The create response carries the only copy of the raw upload link; the
// parent hands it back here so the dialog can offer it for copying.
const showLink = (url, dispatch = null) => {
  createdUrl.value = url;
  dispatchStatus.value = dispatch;
};

const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(createdUrl.value);
    useAlert(t('DOCUMENTS.REQUESTS.COPY_SUCCESS'));
  } catch {
    useAlert(t('DOCUMENTS.REQUESTS.COPY_ERROR'));
  }
};

const submit = async () => {
  if (props.isSubmitting) return;

  const isValid = await v$.value.$validate();
  if (!isValid) return;

  emit('create', {
    contactId: form.value.contactId,
    contactInboxId: form.value.contactInboxId,
    message: form.value.message.trim(),
    expiresInDays: form.value.expiresInDays,
  });
};

defineExpose({ open, close, showLink });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('DOCUMENTS.REQUESTS.DIALOG.TITLE')"
    :description="t('DOCUMENTS.REQUESTS.DIALOG.DESCRIPTION')"
    width="lg"
    :persistent="isSubmitting"
    @close="emit('close')"
  >
    <div v-if="createdUrl" class="flex flex-col gap-4 py-4">
      <p v-if="dispatchStatus?.delivered" class="text-sm text-n-teal-11">
        {{ t('DOCUMENTS.REQUESTS.DIALOG.SENT_VIA_CONVERSATION') }}
      </p>
      <p v-else class="text-sm text-n-slate-11">
        {{ t('DOCUMENTS.REQUESTS.DIALOG.LINK_GUIDANCE') }}
      </p>
      <Input
        :model-value="createdUrl"
        :label="t('DOCUMENTS.REQUESTS.DIALOG.LINK_TITLE')"
        readonly
      />
    </div>
    <div v-else class="flex flex-col gap-4 py-4">
      <ContactPicker
        ref="contactPickerRef"
        v-model="form.contactId"
        :label="t('DOCUMENTS.REQUESTS.DIALOG.RECIPIENT')"
        :placeholder="t('DOCUMENTS.REQUESTS.DIALOG.RECIPIENT_PLACEHOLDER')"
      />
      <ChannelPicker
        v-model="form.contactInboxId"
        :contact-id="form.contactId"
        :label="t('DOCUMENTS.CHANNEL_PICKER.LABEL')"
      />
      <TextArea
        v-model="form.message"
        :label="t('DOCUMENTS.REQUESTS.DIALOG.MESSAGE')"
        :placeholder="t('DOCUMENTS.REQUESTS.DIALOG.MESSAGE_PLACEHOLDER')"
      />
      <Input
        v-model="form.expiresInDays"
        type="number"
        min="1"
        max="90"
        :label="t('DOCUMENTS.REQUESTS.DIALOG.EXPIRES_IN_DAYS')"
        :message="
          v$.expiresInDays.$error ? t('DOCUMENTS.REQUESTS.DIALOG.EXPIRES_IN_DAYS_INVALID') : ''
        "
        :message-type="v$.expiresInDays.$error ? 'error' : 'info'"
      />
    </div>

    <template #footer>
      <div v-if="createdUrl" class="flex justify-end gap-2">
        <Button
          :label="t('DOCUMENTS.REQUESTS.COPY_LINK')"
          icon="i-lucide-copy"
          slate
          @click="copyLink"
        />
        <Button :label="t('DOCUMENTS.REQUESTS.DIALOG.DONE')" @click="close" />
      </div>
      <div v-else class="flex justify-end gap-2">
        <Button
          :label="t('DOCUMENTS.CANCEL')"
          ghost
          slate
          :disabled="isSubmitting"
          @click="close"
        />
        <Button
          :label="t('DOCUMENTS.REQUESTS.DIALOG.SUBMIT')"
          :is-loading="isSubmitting"
          :disabled="isSubmitting"
          @click="submit"
        />
      </div>
    </template>
  </Dialog>
</template>
